import React, { useEffect, useMemo, useState } from 'react';
import { Table, Container, Button } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import ExpenseModal from '../components/ExpenseModal';
import StatusLight from './StatusLight';
import usePermissions from '../utils/usePermissions';
import PaginationControls from '../components/PaginationControls';
import usePagination from '../utils/usePagination';
import { formatCurrency, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';
import '../styles/CustomerList.css';
import '../styles/RecentDocuments.css';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import ListSearchBar from '../components/ListSearchBar';
import useDebouncedValue from '../utils/useDebouncedValue';
import { getActiveSearchTerm, shouldTriggerSearchRequest } from '../utils/searchThreshold';

const ExpenseListPage = () => {
  const { can } = usePermissions('expenses');
  const { showInlineNotice } = useInlineNotice();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isExpenseViewOpen, setIsExpenseViewOpen] = useState(false);
  const [expenseToView, setExpenseToView] = useState(null);
  const [isExpenseEditOpen, setIsExpenseEditOpen] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({ status: null });
  const [sortConfig, setSortConfig] = useState({
    column: 'date',
    direction: 'desc'
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('editDraft'),
    canCancel: can('cancel'),
    canDelete: can('delete'),
    canPayments: can('payments')
  }), [can]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [expenseToCancel, setExpenseToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const activeSearchTerm = getActiveSearchTerm(debouncedSearch);
  const shouldTriggerSearch = shouldTriggerSearchRequest(debouncedSearch);


  const fetchExpenses = async (searchTerm = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/expenses`, {
        params: searchTerm ? { search: searchTerm } : undefined
      });
      const fetchedExpenses = response.data || [];
      setExpenses(fetchedExpenses);
      return fetchedExpenses;
    } catch (err) {
      console.error('Error fetching expenses:', err);
      setError('Failed to load expenses');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldTriggerSearch) {
      return;
    }
    fetchExpenses(activeSearchTerm);
  }, [activeSearchTerm, shouldTriggerSearch]);

  const handleExpenseSaved = () => {
    fetchExpenses(activeSearchTerm);
    setSelectedRows([]);
  };

  const handleCheckboxChange = (expenseId) => {
    setSelectedRows(prev =>
      prev.includes(expenseId)
        ? [] // Deselect if already selected
        : [expenseId] // Select only this row, deselecting any others
    );
  };

  const handleViewExpense = () => {
    if (selectedRows.length === 1) {
      const expense = filteredExpenses.find(exp => exp._id === selectedRows[0]);
      if (expense) {
        setExpenseToView(expense);
        setIsExpenseViewOpen(true);
      }
    }
  };

  const handleEditExpense = () => {
    if (selectedRows.length === 1) {
      if (!permissions.canEdit) return;
      const expense = filteredExpenses.find(exp => exp._id === selectedRows[0]);
      if (expense && (expense.status || 'Draft') === 'Draft') {
        setExpenseToEdit(expense);
        setIsExpenseEditOpen(true);
      }
    }
  };

  const handlePayExpense = () => {
    if (selectedRows.length === 1) {
      if (!permissions.canPayments) return;
      const expense = filteredExpenses.find(exp => exp._id === selectedRows[0]);
      if (expense) {
        setExpenseToView(expense);
        setIsExpenseViewOpen(true);
      }
    }
  };

  const handleDeleteExpense = (expense) => {
    if (!permissions.canDelete) return;
    setExpenseToDelete(expense);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteExpense = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/expenses/${expenseToDelete._id}`);
      const number = expenseToDelete?.expenseNumber || 'expense';
      showInlineNotice(`Expense ${number} deleted successfully.`, { scope: 'expenses' });
      await fetchExpenses(activeSearchTerm);
      setSelectedRows([]);
      setShowDeleteConfirm(false);
      setExpenseToDelete(null);
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError(err.response?.data?.error || 'Failed to delete expense');
      showInlineNotice('Failed to delete expense.', {
        scope: 'expenses',
        type: 'error'
      });
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelDocument = () => {
    if (selectedRows.length !== 1) return;
    if (!permissions.canCancel) return;
    const docToCancel = filteredExpenses.find(doc => doc._id === selectedRows[0]);
    if (docToCancel && ['Draft', 'Confirmed'].includes(docToCancel.status || 'Draft')) {
      setExpenseToCancel(docToCancel);
      setCancelReason('');
      setShowCancelConfirm(true);
    }
  };

  const confirmCancelDocument = async () => {
    try {
      if (!cancelReason.trim()) {
        setError('Cancellation reason is required.');
        return;
      }
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/expenses/${expenseToCancel._id}/cancel`,
        { cancellationReason: cancelReason.trim() },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const number = expenseToCancel?.expenseNumber || 'expense';
      showInlineNotice(`Expense ${number} cancelled successfully.`, { scope: 'expenses' });
      
      await fetchExpenses(activeSearchTerm);
      setShowCancelConfirm(false);
      setExpenseToCancel(null);
      setCancelReason('');
      setSelectedRows([]);
    } catch (err) {
      console.error('Error cancelling expense:', err);
      setError(err.response?.data?.error || 'Failed to cancel expense');
      showInlineNotice('Failed to cancel expense.', {
        scope: 'expenses',
        type: 'error'
      });
      setShowCancelConfirm(false);
    }
  };

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchExpenses(activeSearchTerm);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return formatCurrency(Number(amount) || 0, getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    });
  };

  const getFilteredAndSortedExpenses = () => {
    let filteredExpenses = expenses.filter(expense => {
      if (selectedFilters.status && expense.status !== selectedFilters.status) {
        return false;
      }
      return true;
    });

    // Sort expenses
    filteredExpenses = filteredExpenses.sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.column) {
        case 'expenseNumber':
          aVal = (a.expenseNumber || '').toLowerCase();
          bVal = (b.expenseNumber || '').toLowerCase();
          break;
        case 'amount':
          aVal = parseFloat(a.amount) || 0;
          bVal = parseFloat(b.amount) || 0;
          break;
        case 'date':
          aVal = a.date ? new Date(a.date).getTime() : 0;
          bVal = b.date ? new Date(b.date).getTime() : 0;
          break;
        case 'employee':
          aVal = (a.employee?.name || '').toLowerCase();
          bVal = (b.employee?.name || '').toLowerCase();
          break;
        case 'status':
          aVal = (a.status || 'Submitted').toLowerCase();
          bVal = (b.status || 'Submitted').toLowerCase();
          break;
        case 'totalPaid':
          aVal = parseFloat(a.totalPaid) || 0;
          bVal = parseFloat(b.totalPaid) || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filteredExpenses;
  };

  const handleSort = (column) => {
    if (sortConfig.column === column) {
      // Toggle direction if same column is clicked
      setSortConfig({
        column,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      // Set new column with ascending order
      setSortConfig({
        column,
        direction: 'asc'
      });
    }
  };

  const handleFilterChange = (status) => {
    setSelectedFilters({ status });
  };

  const handleClearFilters = () => {
    setSelectedFilters({ status: null });
  };

  const filteredExpenses = getFilteredAndSortedExpenses();
  const {
    page,
    setPage,
    totalPages,
    pageItems: pagedExpenses,
    totalItems,
    pageSize
  } = usePagination(filteredExpenses, 10);

  useEffect(() => {
    setSelectedRows([]);
  }, [page, activeSearchTerm]);

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Expenses <span className="text-muted">({filteredExpenses.length})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="expenses" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleReload}
            disabled={loading}
            className="recent-invoices-btn recent-invoices-btn-reload"
          >
            {loading ? (
              <>
                <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M11 1a9 9 0 0 1 9 9"/></svg>
                Loading...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                Reload
              </>
            )}
          </button>
          <button
            className="recent-invoices-btn recent-invoices-btn-view"
            onClick={handleViewExpense}
            disabled={selectedRows.length === 0}
            title="View expense details"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            View
          </button>
          <button
            className="recent-invoices-btn recent-invoices-btn-copy"
            onClick={handleEditExpense}
            disabled={
              !permissions.canEdit ||
              selectedRows.length !== 1 ||
              (filteredExpenses.find(exp => exp._id === selectedRows[0])?.status || 'Draft') !== 'Draft'
            }
            title="Edit selected draft expense"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            Edit
          </button>
          <button
            className="recent-invoices-btn recent-invoices-btn-filter"
            onClick={() => setShowFilterModal(true)}
            title="Filter expenses by status"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filter
          </button>
          {permissions.canCancel && (
            <button 
              onClick={() => {
                if (selectedRows.length === 1) {
                  const expense = filteredExpenses.find(exp => exp._id === selectedRows[0]);
                  if (expense && ['Draft', 'Confirmed'].includes(expense.status || 'Draft')) {
                    setExpenseToCancel(expense);
                    setShowCancelConfirm(true);
                  }
                }
              }}
              disabled={
                selectedRows.length !== 1 ||
                !['Draft', 'Confirmed'].includes(filteredExpenses.find(exp => exp._id === selectedRows[0])?.status || 'Draft')
              }
              className="recent-invoices-btn recent-invoices-btn-cancel"
              title="Cancel selected expense"
              style={{ backgroundColor: '#f97316', color: 'white' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              Cancel
            </button>
          )}
          {permissions.canDelete && (
            <button
              onClick={() => {
                if (selectedRows.length === 1) {
                  handleDeleteExpense(filteredExpenses.find(exp => exp._id === selectedRows[0]));
                }
              }}
              disabled={selectedRows.length === 0}
              className="recent-invoices-btn recent-invoices-btn-delete"
              title="Delete selected expense"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
              Delete
            </button>
          )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ListSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by Expense #, Type, Employee"
                disabled={loading}
                isSearching={loading && Boolean(activeSearchTerm)}
              />
              {permissions.canCreate && (
                <button
                  onClick={() => setIsExpenseModalOpen(true)}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredExpenses.length === 0 ? (
          <div className="recent-invoices-empty">
            {selectedFilters.status
              ? 'No expenses match the selected filter'
              : activeSearchTerm
                ? 'No expenses match your search'
                : 'No expenses yet'}
            {selectedFilters.status && (
              <button 
                onClick={handleClearFilters}
                style={{
                  marginTop: '10px',
                  padding: '6px 12px',
                  backgroundColor: '#0ea5e9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <table className="recent-invoices-table">
            <thead>
              <tr>
                <th></th>
                <th onClick={() => handleSort('expenseNumber')} className={`sortable ${sortConfig.column === 'expenseNumber' ? 'sorted' : ''}`}>
                  Expense # {sortConfig.column === 'expenseNumber' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('type')} className={`sortable ${sortConfig.column === 'type' ? 'sorted' : ''}`}>
                  Type {sortConfig.column === 'type' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('amount')} className={`sortable ${sortConfig.column === 'amount' ? 'sorted' : ''}`}>
                  Amount {sortConfig.column === 'amount' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('date')} className={`sortable ${sortConfig.column === 'date' ? 'sorted' : ''}`}>
                  Date {sortConfig.column === 'date' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('employee')} className={`sortable ${sortConfig.column === 'employee' ? 'sorted' : ''}`}>
                  Employee {sortConfig.column === 'employee' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('totalPaid')} className={`sortable ${sortConfig.column === 'totalPaid' ? 'sorted' : ''}`}>
                  Total Paid {sortConfig.column === 'totalPaid' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('status')} className={`sortable ${sortConfig.column === 'status' ? 'sorted' : ''}`}>
                  Status {sortConfig.column === 'status' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedExpenses.map((expense) => {
                const totalPaid = expense.totalPaid || 0;
                const remaining = expense.amount - totalPaid;
                const isPaid = expense.status === 'Completed';

                return (
                  <tr 
                    key={expense._id}
                    className={selectedRows.includes(expense._id) ? 'selected' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(expense._id)}
                        onChange={() => handleCheckboxChange(expense._id)}
                      />
                    </td>
                    <td style={{fontWeight: '500'}}>{expense.expenseNumber || '-'}</td>
                    <td>{expense.type || 'Not Specified'}</td>
                    <td>{formatAmount(expense.amount)}</td>
                    <td>{formatDate(expense.date)}</td>
                    <td>
                      <div>{expense.employee?.name || 'Unknown'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        {expense.employee?.email || ''}
                      </div>
                    </td>
                    <td>
                      <div>{formatAmount(totalPaid)}</div>
                      {!isPaid && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Remaining: {formatAmount(remaining)}
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusLight status={expense.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>

      <ExpenseModal
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        onExpenseSaved={handleExpenseSaved}
      />

      <ExpenseModal
        isOpen={isExpenseViewOpen}
        onClose={() => {
          setIsExpenseViewOpen(false);
          setExpenseToView(null);
        }}
        expense={expenseToView}
        onExpenseSaved={handleExpenseSaved}
      />

      <ExpenseModal
        isOpen={isExpenseEditOpen}
        onClose={() => {
          setIsExpenseEditOpen(false);
          setExpenseToEdit(null);
        }}
        expense={expenseToEdit}
        isEditable
        onExpenseSaved={handleExpenseSaved}
      />

      {/* Filter Modal */}
      {showFilterModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowFilterModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              Filter Expenses
            </h2>

            {/* Status Filter */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Status
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {['Draft', 'Confirmed', 'In Progress', 'Completed', 'Cancelled'].map(status => (
                  <button
                    key={status}
                    onClick={() => handleFilterChange(selectedFilters.status === status ? null : status)}
                    style={{
                      padding: '10px',
                      backgroundColor: selectedFilters.status === status ? '#0ea5e9' : '#f3f4f6',
                      border: selectedFilters.status === status ? '2px solid #0284c7' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      color: selectedFilters.status === status ? 'white' : '#374151',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear and Close Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937',
                  transition: 'all 0.2s ease'
                }}
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#0ea5e9',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s ease'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && expenseToCancel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowCancelConfirm(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
              Cancel Expense
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to cancel this expense ({expenseToCancel.type})? This will mark the expense as cancelled.
            </p>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Cancellation Reason
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '16px',
                resize: 'vertical'
              }}
              placeholder="Enter reason for cancellation"
            />
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowCancelConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937'
                }}
              >
                No, Keep It
              </button>
              <button
                onClick={confirmCancelDocument}
                disabled={!cancelReason.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f97316',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: cancelReason.trim() ? 'pointer' : 'not-allowed',
                  color: 'white'
                }}
              >
                Yes, Cancel Expense
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && expenseToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowDeleteConfirm(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
              Delete Expense
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to delete this <strong>{expenseToDelete.type}</strong> expense of <strong>{formatAmount(expenseToDelete.amount)}</strong>? This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteExpense}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseListPage;
